"use client";

import React from "react";
import { Box, Stack, useMediaQuery } from "@mui/material";
import { ColletteCarousel } from "@collette-travel/collette-common-controls";
import { SectionHeader } from "@/components/common/section-header";
import RelatedBlogCard from "@/components/product-detail-page/related-blogs/related-blog-card";
import { RelatedBlogPost } from "@/types/contentful/blog";
import { RelatedBlogsSection } from "@/types/dto/landing-page/landing-page";
import {
  isDarkLandingPageBackground,
  resolveLandingPageSectionBackgroundColor
} from "@/utils/landing-page-background";
import RelatedBlogsSkeleton from "./related-blogs-skeleton";

interface RelatedBlogsPdpProps {
  blogSection: RelatedBlogsSection;
  moduleRankingPosition?: string;
}

const RelatedBlogsPdp: React.FC<RelatedBlogsPdpProps> = ({
  blogSection,
  moduleRankingPosition = "18"
}) => {

  const {basicInfo, blogList} = blogSection ?? {};
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const resolvedBackgroundColor = resolveLandingPageSectionBackgroundColor(blogSection?.backgroundColor, "transparent");
  const isDarkBackground = isDarkLandingPageBackground(blogSection?.backgroundColor);

  if (!blogList || blogList.length === 0) {
    return <RelatedBlogsSkeleton headerText="Related Blogs" />;
  }

  const resolveArrows = ():  "bottom" | "none" | "sides" => {
    if (isDesktop) return blogList.length > 3 ? "sides" : "none";
    if (isMobile) return blogList.length > 1 ? "bottom" : "none";
    return blogList.length > 2 ? "sides" : "none";
  };

  return (
    <Stack
      component="section"
      data-track="visibility"
      data-parent-container-type="pdp"
      data-module-name="Related Blogs Section"
      data-module-ranking-position={moduleRankingPosition}
      data-testid="related-blogs-section"
      spacing="40px"
      sx={{
        backgroundColor: resolvedBackgroundColor,
        padding: { xs: "4rem 2.4rem", md: "4rem 3.2rem", lg: "4rem 10rem" }
      }}
      alignItems="center"
    >
      <SectionHeader
        headerLevel="h2"
        headerText={basicInfo.headerText || "Related Blogs"}
        headerSx={{
          textTransform: "none !important",
          textAlign: "center",
          color: isDarkBackground ? "primary.contrastText" : "text.primary"
        }}
        headerTestId="related-blogs-section-header"
        copyText={basicInfo.copyText || ""}
        copySx={{
          textAlign: "center",
          color: isDarkBackground ? "primary.contrastText" : "text.primary"
        }}
      />
      <Box
        sx={{
          maxWidth: { xs: "30rem", md: "62rem", lg: "94rem" }
        }}
      >
        <ColletteCarousel
          arrowPosition={resolveArrows()}
          testId="related-posts-carousel"
          variant="multi-card"
          slidesToScroll="auto"
          arrowContainerSx={{ right: isDesktop ? "70px" : 0 }}
          showDots={resolveArrows() !== "none"}
        >
          {blogList.map((item: RelatedBlogPost, index: number) => (
            <RelatedBlogCard item={item} key={item.id} index={index} />
          ))}
        </ColletteCarousel>
      </Box>
    </Stack>
  );
};

export default RelatedBlogsPdp;
